import { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";
import DemoMallShops from "../components/DemoMallShops";

import {
  createLayout,
  eligibleStores,
  placeStores,
  validCapacity,
} from "../utils/dynamicLayout";

function activeStore(s) {
  const value = s.storePackageExpiresAt;

  const expiry =
    value?.toMillis?.() ||
    (value?.seconds
      ? value.seconds * 1000
      : value
        ? new Date(value).getTime()
        : 0);

  return (
    !s.adminDisabled &&
    !s.adminDeleted &&
    s.hasStorePackage !== false &&
    s.storePackageStatus !== "expired" &&
    !(expiry && expiry < Date.now())
  );
}

async function loadStores() {
  const snapshot = await getDocs(
    query(collection(db, "stores"), where("published", "==", true)),
  );

  return eligibleStores(
    snapshot.docs.map((d) => ({
      ...d.data(),
      id: d.id,
    })),
    activeStore,
  );
}

export default function VirtualMallSettings() {
  const [capacity, setCapacity] = useState("8");
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;

    Promise.all([getDoc(doc(db, "mall_settings", "public")), loadStores()])
      .then(([settings, stores]) => {
        if (!alive) return;

        const value = settings.data()?.shopsPerFloor;

        setCapacity(String(validCapacity(value) ? value : 8));
        setCount(stores.length);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;

        setFailed(true);
        setLoading(false);
        setMessage(
          "Unable to load settings. Check admin access and Firestore rules, then reload.",
        );
      });

    return () => {
      alive = false;
    };
  }, []);

  const preview = createLayout(
    count,
    validCapacity(capacity) ? Number(capacity) : 8,
  );

  async function save(event) {
    event.preventDefault();

    if (!validCapacity(capacity)) {
      setMessage("Enter a whole number from 4 to 100.");
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      const stores = await loadStores();
      const layout = createLayout(stores.length, Number(capacity));

      await runTransaction(db, async (transaction) => {
        const ref = doc(db, "mall_settings", "public");
        const current = await transaction.get(ref);

        const slots = placeStores(
          layout.slots,
          stores,
          [],
          current.data()?.assignments || {},
        );

        const assignments = Object.fromEntries(
          slots.filter((s) => s.shopId).map((s) => [s.shopId, s.slotId]),
        );

        transaction.set(ref, {
          shopsPerFloor: Number(capacity),
          assignments,
          updatedAt: serverTimestamp(),
        });
      });

      setCount(stores.length);
      setMessage(
        `Saved. ${stores.length} eligible shops across ` +
          `${layout.wingCount} wings, up to ` +
          `${layout.floorCount} floors each.`,
      );
    } catch (error) {
      setMessage(`Save failed: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="max-w-2xl rounded-2xl bg-white p-6 shadow">
      <h1 className="text-2xl font-bold">3D Mall Settings</h1>

      <p className="mt-2 text-gray-600">
        Each wing has at most three floors and ten shop spaces per level. Higher
        capacities spread across connected wings. After three levels, additional
        shops create another wing group.
      </p>

      <form onSubmit={save} className="mt-6 space-y-4">
        <label className="block font-medium" htmlFor="mall-capacity">
          Shop spaces per level — across one wing group
        </label>

        <input
          id="mall-capacity"
          type="number"
          min="4"
          max="100"
          step="1"
          required
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          disabled={loading || saving || failed}
          className="w-full rounded-lg border p-3"
        />

        <p>
          {loading
            ? "Loading…"
            : `${count} eligible shops → ` +
              `${preview.wingCount} wings, up to ` +
              `${preview.floorCount} floors each`}
        </p>

        <p className="text-sm text-gray-600">
          Disabled, deleted, unpublished and expired shops are excluded.
          Existing placements survive when their slots remain available.
        </p>

        <button
          type="submit"
          disabled={loading || saving || failed || !validCapacity(capacity)}
          className="rounded-lg bg-indigo-600 px-5 py-3 text-white disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Mall Settings"}
        </button>

        {message && <p role="status">{message}</p>}
      </form>

      <DemoMallShops
        onChanged={() => {
          loadStores()
            .then((stores) => setCount(stores.length))
            .catch((error) => {
              setMessage(`Unable to refresh shop count: ${error.message}`);
            });
        }}
      />
    </section>
  );
}
